/**
 * Class Detail Page Route Definition
 * Top-level route: /class/:classId
 *
 * validateSearch/loader/head stay here for SSR; the UI lives in
 * $classId.lazy.tsx so the fixture/skeleton graph stays out of the entry chunk.
 */
import { createFileRoute } from '@tanstack/react-router';
import { ClassDetailTemplatePending } from '@/components/v2/page-template-pending';
import { cacheFirst } from '@/lib/queries/cache-first';
import { classDetailQuery, type ClassQueryData } from '@/lib/queries/detail';
import { entityHeadMeta } from '@/lib/utils/site-meta';

type ClassDetailResult<T> = { ok: true; data: T } | { ok: false };

const toClassDetailResult = async <T,>(promise: Promise<T>): Promise<ClassDetailResult<T>> => {
  try {
    const data = await promise;
    return typeof data === 'object' && data !== null && 'ok' in data && data.ok === false
      ? { ok: false }
      : { ok: true, data };
  } catch {
    return { ok: false };
  }
};

export const Route = createFileRoute('/_public/class/$classId')({
  // Template-first pending: while this route's component chunk downloads, show the
  // class page's own shape (full-bleed cover → cards → sticky Book Now bar, no
  // back header) instead of the full-page RoutePending mascot (audit part 2: a
  // detail click must switch immediately, skeletons included).
  pendingComponent: ClassDetailTemplatePending,
  // Hand-rolled like discovery.tsx/feature-requests: this route-def file is in
  // the eager chunk for every page, and a zod import here pins zod into it.
  // The optional-key return type keeps `search` optional for <Link>/navigate,
  // exactly as the previous z.object({ returnTo: z.string().optional() }) did.
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => {
    if (typeof search.returnTo !== 'string') return {};
    return { returnTo: search.returnTo };
  },
  loader: async ({ context, params, preload }) => {
    // Key + queryFn come from the shared factory (app/lib/queries/detail.ts) so
    // the loader-seeded entry, the detail page's useQuery and the data
    // prefetchers can never drift apart. Behavior is byte-for-byte the inline
    // shape this used to spell out.
    const classQuery = classDetailQuery(params.classId);
    const refreshClass = () => context.queryClient.ensureQueryData(classQuery);
    // Preload short-circuit: detail-entry cards fire this loader via
    // <Link preload="viewport"> as they scroll past, and a preload run must
    // never touch the network. Serve the cache when present, else an
    // instantly-resolved cold ({ ok: false }) envelope. The result must
    // SETTLE, never park: preloadRoute keeps the loader lane, its abort
    // controller and match resources alive until the loader settles, so a
    // never-resolving promise would leak a permanently-pending lane for
    // every card scrolled past. The ok:false value is never rendered —
    // commits always re-run this loader (defaultPreloadStaleTime is 0);
    // warming the route chunk is all a preload exists for here.
    // getQueryData is optional-chained for the loader-contract tests, which
    // stub the client with ensureQueryData only; a missing reader simply
    // reads as cold cache.
    if (preload) {
      const cached = context.queryClient.getQueryData?.<ClassQueryData>(classQuery.queryKey);
      return {
        classDetailPromise:
          cached === undefined
            ? Promise.resolve<ClassDetailResult<ClassQueryData>>({ ok: false })
            : toClassDetailResult(Promise.resolve(cached)),
        classDetail: undefined,
      };
    }
    // Cache-first gate: the shared gate (app/lib/queries/data-plane, through
    // cacheFirst) owns the freshness decision, so this loader and the
    // prefetchers can never disagree about whether the cache entry is usable.
    // A cached class resolves the envelope immediately and a stale entry is
    // healed in the background (single-flight-deduped with the mounted
    // queries); a cold cache keeps the streamed fetch below, with its existing
    // retry/error paths.
    const { cached, stream } = cacheFirst(context.queryClient, classQuery.queryKey, refreshClass);
    const classDetailPromise =
      cached !== undefined
        ? toClassDetailResult(Promise.resolve(cached))
        : toClassDetailResult(stream());
    // Await before returning ONLY on the server, so head() can read the entity
    // for SSR OG tags (audit UIUX-02): chat-app crawlers never run JS, so a
    // streamed title would never reach the preview. Failures resolve to
    // ok:false so the body keeps its retry UI instead of the error boundary.
    // Client navigations skip the await — the un-awaited promise streams into
    // the lazy component's <Await> (EntityDetailLayout skeleton) and head()
    // falls back to the root defaults while the entity is absent (perf audit
    // part 2, Theme 2: the await blocked every client-side navigation).
    if (typeof document !== 'undefined') {
      return { classDetailPromise, classDetail: undefined };
    }
    const classDetail = await classDetailPromise;
    const classDetailData =
      classDetail.ok && classDetail.data.ok ? classDetail.data.value : undefined;
    return { classDetailPromise, classDetail: classDetailData };
  },
  head: ({ loaderData, params }) => {
    const classDetail = loaderData?.classDetail;
    if (!classDetail) return {};
    const where = classDetail.studio?.name ?? classDetail.instructor?.name;
    return {
      ...entityHeadMeta({
        title: `${classDetail.name} - Class | TIL`,
        description:
          classDetail.description ||
          (where
            ? `${classDetail.name} at ${where} - see details and book a spot on TIL.`
            : `See ${classDetail.name} and book a spot on TIL.`),
        image: classDetail.studio?.logoUrl ?? classDetail.instructor?.imageUrl,
        path: `/class/${params.classId}`,
      }),
    };
  },
});
